import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "../../lib/api";
import { toast } from "sonner";
import { fmtTRY } from "../../lib/format";
import { errText } from "../../lib/errors";
import { ChevronLeft, ChevronRight, Plus, Trash2, CalendarClock, ArrowDownLeft, ArrowUpRight, FileText, StickyNote, CreditCard, ReceiptText, CheckCircle2, BellRing, MessageCircle, Landmark } from "lucide-react";

const tl = fmtTRY;
const GUNLER = ["Pzt", "Sal", "Çar", "Per", "Cum", "Cmt", "Paz"];
const AY_AD = ["Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran", "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık"];

const KIND_STYLE = {
  tahsilat: { dot: "bg-emerald-500", badge: "bg-emerald-50 text-emerald-800 border-emerald-200", label: "Tahsilat (Planlanan)", Icon: ArrowDownLeft },
  odeme: { dot: "bg-rose-500", badge: "bg-rose-50 text-rose-800 border-rose-200", label: "Ödeme (Planlanan)", Icon: ArrowUpRight },
  fatura: { dot: "bg-sky-500", badge: "bg-sky-50 text-sky-800 border-sky-200", label: "Fatura Vadesi", Icon: ReceiptText },
  cek_alinan: { dot: "bg-indigo-500", badge: "bg-indigo-50 text-indigo-800 border-indigo-200", label: "Çek/Senet (Alınan)", Icon: FileText },
  cek_verilen: { dot: "bg-indigo-300", badge: "bg-indigo-50 text-indigo-800 border-indigo-200", label: "Çek/Senet (Verilen)", Icon: FileText },
  kart_taksit: { dot: "bg-amber-500", badge: "bg-amber-50 text-amber-800 border-amber-200", label: "Kart Taksiti", Icon: CreditCard },
  islem: { dot: "bg-stone-400", badge: "bg-stone-100 text-stone-600 border-stone-300", label: "Gerçekleşen İşlem", Icon: Landmark },
  not: { dot: "bg-gold", badge: "bg-amber-50 text-amber-800 border-amber-200", label: "Plan / Not", Icon: StickyNote },
};
const SRC_LBL = { plan: "Ödeme Planı", cek: "Çek/Senet", kart: "Kart Taksiti", fatura: "Fatura" };
const inputCls = "w-full h-10 px-3 rounded-lg border border-stone-300 text-sm focus:outline-none focus:ring-2 focus:ring-forest/30";

export default function AdminCalendar() {
  const today = new Date().toISOString().slice(0, 10);
  const [month, setMonth] = useState(today.slice(0, 7));
  const [days, setDays] = useState({});
  const [selected, setSelected] = useState(today);
  const [evForm, setEvForm] = useState({ title: "", note: "" });
  const [busy, setBusy] = useState(false);
  const [filters, setFilters] = useState(() => Object.fromEntries(Object.keys(KIND_STYLE).map((k) => [k, true])));
  const [reminders, setReminders] = useState(null);
  const [showAll, setShowAll] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/calendar", { params: { month } }).then(({ data }) => setDays(data.days || {})).catch(() => toast.error("Takvim yüklenemedi"));
  }, [month]);
  const loadReminders = useCallback(() => {
    api.get("/admin/reminders").then(({ data }) => setReminders(data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadReminders(); }, [loadReminders]);

  const shift = (dir) => {
    const [y, m] = month.split("-").map(Number);
    const d = new Date(y, m - 1 + dir, 1);
    setMonth(d.toISOString().slice(0, 7));
  };

  const cells = useMemo(() => {
    const [y, m] = month.split("-").map(Number);
    const first = new Date(y, m - 1, 1);
    const lead = (first.getDay() + 6) % 7;
    const count = new Date(y, m, 0).getDate();
    const arr = Array(lead).fill(null);
    for (let d = 1; d <= count; d++) arr.push(`${month}-${String(d).padStart(2, "0")}`);
    return arr;
  }, [month]);

  const visible = useCallback((items) => (items || []).filter((i) => filters[i.kind] !== false), [filters]);

  const monthStats = useMemo(() => {
    let planIn = 0, planOut = 0, realIn = 0, realOut = 0, over = 0;
    Object.entries(days).forEach(([, items]) => visible(items).forEach((i) => {
      if (i.overdue && i.direction) over += 1;
      if (i.realized) { i.direction === "in" ? (realIn += i.amount) : i.direction === "out" && (realOut += i.amount); }
      else { i.direction === "in" ? (planIn += i.amount) : i.direction === "out" && (planOut += i.amount); }
    }));
    return { planIn, planOut, realNet: realIn - realOut, over };
  }, [days, visible]);

  const selItems = visible(days[selected]);
  const selIn = selItems.filter((i) => i.direction === "in").reduce((s, i) => s + i.amount, 0);
  const selOut = selItems.filter((i) => i.direction === "out").reduce((s, i) => s + i.amount, 0);

  const addEvent = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/admin/calendar/events", { date: selected, ...evForm });
      toast.success("Güne plan/not eklendi");
      setEvForm({ title: "", note: "" });
      load();
    } catch (err2) { toast.error(errText(err2) || "Eklenemedi"); }
    finally { setBusy(false); }
  };

  const delEvent = async (id) => {
    try {
      await api.post(`/admin/calendar/events/${id}/sil`);
      toast.success("Silindi");
      load();
    } catch { toast.error("Silinemedi"); }
  };

  const markDone = async (r) => {
    try {
      await api.post(`/admin/reminders/${r.plan_id}/${r.no}/done`);
      toast.success("Hatırlatma işlendi");
      loadReminders();
    } catch { toast.error("İşlenemedi"); }
  };

  const monthLabel = `${AY_AD[parseInt(month.slice(5, 7)) - 1]} ${month.slice(0, 4)}`;
  const overdue = reminders?.overdue || [];
  const upcoming = reminders?.upcoming || [];

  return (
    <div data-testid="admin-calendar-page" className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-serif text-2xl sm:text-3xl text-forest">Takvim</h1>
          <p className="text-stone-500 text-sm mt-1">Geçmiş işlemler, gelecek vadeler, çek/senet, kart taksitleri ve hatırlatıcılar — tek ekranda.</p>
        </div>
        <div className="flex gap-2">
          <button data-testid="calendar-today" onClick={() => { setMonth(today.slice(0, 7)); setSelected(today); }} className="text-xs font-bold border border-stone-300 text-stone-700 hover:border-forest px-3.5 py-2.5 rounded-lg transition-colors">Bugün</button>
          <Link to="/admin/odeme-planlari" data-testid="calendar-goto-plans" className="flex items-center gap-1.5 text-xs font-bold bg-forest hover:bg-forest-dark text-white px-3.5 py-2.5 rounded-lg transition-colors"><CalendarClock className="w-3.5 h-3.5" /> Ödeme Planları</Link>
        </div>
      </div>

      {/* Ay özeti */}
      <div data-testid="calendar-summary" className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[
          { l: "Planlanan Tahsilat", v: `+${tl(monthStats.planIn)} ₺`, cls: "text-emerald-700", tid: "calendar-sum-planin" },
          { l: "Planlanan Ödeme", v: `-${tl(monthStats.planOut)} ₺`, cls: "text-terra", tid: "calendar-sum-planout" },
          { l: "Gerçekleşen Net (Ay)", v: `${monthStats.realNet >= 0 ? "+" : "-"}${tl(Math.abs(monthStats.realNet))} ₺`, cls: monthStats.realNet >= 0 ? "text-emerald-700" : "text-terra", tid: "calendar-sum-realnet" },
          { l: "Geciken Kalem", v: monthStats.over, cls: monthStats.over > 0 ? "text-red-600" : "text-stone-700", tid: "calendar-sum-overdue" },
        ].map((s) => (
          <div key={s.l} data-testid={s.tid} className="bg-white rounded-xl border border-stone-200/80 shadow-sm px-4 py-3">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-stone-400">{s.l}</p>
            <p className={`text-lg font-bold mt-0.5 ${s.cls}`}>{s.v}</p>
          </div>
        ))}
      </div>

      {/* Tür filtreleri */}
      <div data-testid="calendar-filters" className="flex flex-wrap gap-2">
        {Object.entries(KIND_STYLE).map(([k, s]) => (
          <button key={k} data-testid={`calendar-filter-${k}`} onClick={() => setFilters({ ...filters, [k]: !filters[k] })}
            className={`flex items-center gap-1.5 text-[11px] font-bold px-3 py-1.5 rounded-full border transition-colors ${filters[k] ? "bg-white border-stone-300 text-stone-700" : "bg-stone-100 border-stone-200 text-stone-400 line-through"}`}>
            <span className={`w-2 h-2 rounded-full ${s.dot}`} /> {s.label}
          </button>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-6 items-start">
        {/* Ay ızgarası */}
        <div className="lg:col-span-2 bg-white rounded-xl border border-[#EDE8DE] shadow-sm p-4">
          <div className="flex items-center justify-between mb-3">
            <button data-testid="calendar-prev" onClick={() => shift(-1)} className="p-2 hover:bg-stone-100 rounded-lg"><ChevronLeft className="w-4 h-4" /></button>
            <p data-testid="calendar-month-label" className="font-semibold text-stone-800">{monthLabel}</p>
            <button data-testid="calendar-next" onClick={() => shift(1)} className="p-2 hover:bg-stone-100 rounded-lg"><ChevronRight className="w-4 h-4" /></button>
          </div>
          <div className="grid grid-cols-7 gap-1 text-center text-[11px] font-bold uppercase tracking-wider text-stone-400 mb-1">
            {GUNLER.map((g) => <span key={g}>{g}</span>)}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {cells.map((date, idx) => {
              if (!date) return <div key={`b${idx}`} />;
              const items = visible(days[date]);
              const isSel = date === selected;
              const isToday = date === today;
              const hasOverdue = items.some((i) => i.overdue && i.direction);
              return (
                <button key={date} data-testid={`calendar-day-${date}`} onClick={() => setSelected(date)}
                  className={`min-h-[72px] sm:min-h-[84px] rounded-lg border p-1.5 text-left transition-colors ${isSel ? "border-forest bg-forest/5" : "border-stone-200 hover:border-forest/50"} ${isToday ? "ring-1 ring-gold" : ""} ${hasOverdue ? "bg-red-50/60 border-red-200" : ""}`}>
                  <span className={`text-xs font-bold ${isToday ? "text-gold-dark" : "text-stone-700"}`}>{parseInt(date.slice(8, 10))}</span>
                  <span className="block mt-0.5 space-y-0.5">
                    {items.slice(0, 2).map((it, i) => (
                      <span key={i} className={`flex items-center gap-1 min-w-0 ${it.realized ? "opacity-50" : ""}`}>
                        <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${(KIND_STYLE[it.kind] || KIND_STYLE.not).dot}`} />
                        <span className="text-[9px] text-stone-600 truncate">{it.title}</span>
                      </span>
                    ))}
                    {items.length > 2 && <span className="block text-[9px] text-stone-400 font-bold">+{items.length - 2} kayıt</span>}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Sağ kolon: gün detayı + hatırlatıcılar + not ekleme */}
        <div className="space-y-4">
          <div className="bg-white rounded-xl border border-[#EDE8DE] shadow-sm p-5">
            <div className="flex items-center justify-between">
              <h2 className="font-semibold text-forest">{selected.split("-").reverse().join(".")}</h2>
              <div className="text-[11px] space-x-2">
                {selIn > 0 && <span data-testid="calendar-day-in" className="font-bold text-emerald-700">+{tl(selIn)} ₺</span>}
                {selOut > 0 && <span data-testid="calendar-day-out" className="font-bold text-terra">-{tl(selOut)} ₺</span>}
              </div>
            </div>
            <div data-testid="calendar-day-items" className="mt-3 space-y-2 max-h-[280px] overflow-y-auto pr-1">
              {selItems.length === 0 && <p className="text-sm text-stone-400 text-center py-6">Bu gün için kayıt yok.</p>}
              {selItems.map((it, i) => {
                const m = KIND_STYLE[it.kind] || KIND_STYLE.not;
                return (
                  <div key={i} data-testid={`calendar-item-${i}`} className={`flex items-center gap-2.5 border rounded-lg px-3 py-2 ${it.overdue && it.direction ? "border-red-300 bg-red-50" : "border-stone-200"} ${it.realized ? "opacity-60" : ""}`}>
                    <m.Icon className="w-3.5 h-3.5 text-stone-400 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold text-stone-800 truncate">{it.title}{it.overdue && it.direction ? <b className="text-red-600"> • gecikti</b> : ""}{it.realized ? <span className="text-stone-400 font-normal"> • gerçekleşti</span> : ""}</p>
                      <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full border ${m.badge}`}>{m.label}</span>
                      {it.note && <p className="text-[11px] text-stone-400 mt-0.5">{it.note}</p>}
                    </div>
                    {it.amount > 0 && <p className={`text-xs font-bold shrink-0 ${it.direction === "in" ? "text-emerald-700" : "text-terra"}`}>{it.direction === "in" ? "+" : "-"}{tl(it.amount)} ₺</p>}
                    {it.event_id && <button data-testid={`calendar-event-del-${it.event_id}`} onClick={() => delEvent(it.event_id)} className="p-1 hover:bg-red-50 rounded shrink-0"><Trash2 className="w-3.5 h-3.5 text-red-400" /></button>}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Hatırlatıcılar */}
          <div data-testid="calendar-reminders" className="bg-white rounded-xl border border-[#EDE8DE] shadow-sm p-5">
            <h2 className="font-semibold text-forest text-sm flex items-center gap-2"><BellRing className="w-4 h-4" /> Hatırlatıcılar
              {overdue.length > 0 && <span data-testid="reminder-overdue-count" className="text-[10px] font-bold bg-red-100 text-red-700 px-2 py-0.5 rounded-full">{overdue.length} geciken</span>}
            </h2>
            {reminders === null && <p className="text-xs text-stone-400 mt-3">Yükleniyor...</p>}
            {reminders && overdue.length === 0 && upcoming.length === 0 && <p className="text-xs text-stone-400 mt-3 flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" /> Geciken veya yaklaşan vade yok.</p>}
            {overdue.length > 0 && (
              <div className="mt-3 space-y-1.5">
                <p className="text-[10px] font-bold uppercase tracking-wider text-red-500">Gecikenler</p>
                {(showAll ? overdue : overdue.slice(0, 4)).map((r, i) => (
                  <div key={i} data-testid={`reminder-item-${i}`} className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg px-2.5 py-2">
                    <div className="flex-1 min-w-0">
                      <p className="text-[11px] font-semibold text-stone-800 truncate">{r.title}</p>
                      <p className="text-[10px] text-red-600 font-semibold">{r.days_overdue} gün gecikti · {SRC_LBL[r.source]}</p>
                    </div>
                    <p className="text-[11px] font-bold text-terra shrink-0">{tl(r.amount)} ₺</p>
                    {r.phone && <a data-testid={`reminder-wa-${i}`} href={`https://wa.me/${r.phone.replace(/\D/g, "")}?text=${encodeURIComponent(`Sayın ${r.title.split("—")[0].trim()}, ${tl(r.amount)} ₺ tutarlı vadeniz ${r.days_overdue} gün önce dolmuştur. Ödemenizi hatırlatmak isteriz. — Ata Angora Doğal Gıdalar`)}`} target="_blank" rel="noreferrer" className="p-1.5 hover:bg-emerald-100 rounded-lg shrink-0" title="WhatsApp ile hatırlat"><MessageCircle className="w-3.5 h-3.5 text-emerald-700" /></a>}
                    {r.plan_id && <button data-testid={`reminder-done-${i}`} onClick={() => markDone(r)} className="p-1.5 hover:bg-stone-100 rounded-lg shrink-0" title="Hatırlatıldı olarak işaretle"><CheckCircle2 className="w-3.5 h-3.5 text-stone-500" /></button>}
                  </div>
                ))}
              </div>
            )}
            {upcoming.length > 0 && (
              <div className="mt-3 space-y-1.5">
                <p className="text-[10px] font-bold uppercase tracking-wider text-amber-600">Önümüzdeki 7 Gün</p>
                {(showAll ? upcoming : upcoming.slice(0, 3)).map((r, i) => (
                  <div key={i} data-testid={`reminder-upcoming-${i}`} className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-2">
                    <div className="flex-1 min-w-0">
                      <p className="text-[11px] font-semibold text-stone-800 truncate">{r.title}</p>
                      <p className="text-[10px] text-amber-700 font-semibold">{r.days_left === 0 ? "Bugün" : `${r.days_left} gün kaldı`} · {SRC_LBL[r.source]}</p>
                    </div>
                    <p className="text-[11px] font-bold text-stone-700 shrink-0">{tl(r.amount)} ₺</p>
                  </div>
                ))}
              </div>
            )}
            {(overdue.length > 4 || upcoming.length > 3) && (
              <button data-testid="reminder-show-all" onClick={() => setShowAll(!showAll)} className="mt-2 w-full text-[11px] font-bold text-forest hover:underline">{showAll ? "Daha az göster" : "Tümünü göster"}</button>
            )}
          </div>

          <form onSubmit={addEvent} className="bg-white rounded-xl border border-[#EDE8DE] shadow-sm p-5 space-y-3">
            <h2 className="font-semibold text-forest text-sm">Bu Güne Plan / Not Ekle</h2>
            <input data-testid="calendar-event-title" required className={inputCls} placeholder="Başlık (ör. Tedarikçi görüşmesi)" value={evForm.title} onChange={(e) => setEvForm({ ...evForm, title: e.target.value })} />
            <textarea data-testid="calendar-event-note" rows={2} className="w-full px-3 py-2 rounded-lg border border-stone-300 text-sm focus:outline-none focus:ring-2 focus:ring-forest/30" placeholder="Not (opsiyonel)" value={evForm.note} onChange={(e) => setEvForm({ ...evForm, note: e.target.value })} />
            <button data-testid="calendar-event-add" disabled={busy} className="w-full flex items-center justify-center gap-1.5 bg-forest hover:bg-forest-dark disabled:bg-stone-400 text-white text-sm font-bold py-2.5 rounded-lg transition-colors"><Plus className="w-4 h-4" /> {busy ? "Ekleniyor..." : "Ekle"}</button>
            <p className="text-[11px] text-stone-400">Taksitli ödeme planı için <Link to="/admin/odeme-planlari" className="font-semibold text-forest underline">Ödeme Planları</Link> bölümünü kullanın.</p>
          </form>
        </div>
      </div>
    </div>
  );
}
